import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, type Ref } from 'vue'

export const calculateBetaCanvasScale = (availableWidth: number, logicalWidth: number) => {
    if (!Number.isFinite(availableWidth) || !Number.isFinite(logicalWidth)) return 1
    if (availableWidth <= 0 || logicalWidth <= 0) return 1

    return Math.min(1, availableWidth / logicalWidth)
}

export const calculateBetaCanvasScaledDimension = (dimension: number, scale: number) => {
    if (!Number.isFinite(dimension) || !Number.isFinite(scale)) return 0
    if (dimension <= 0 || scale <= 0) return 0

    return Math.ceil(dimension * scale)
}

type UseBetaCanvasScaleOptions = {
    viewportRef: Ref<HTMLElement | null>
    contentRef: Ref<HTMLElement | null>
    logicalWidth: Ref<number>
}

export const useBetaCanvasScale = ({ viewportRef, contentRef, logicalWidth }: UseBetaCanvasScaleOptions) => {
    const availableWidth = ref(0)
    const contentHeight = ref(0)
    let resizeObserver: ResizeObserver | null = null
    let hasWindowResizeFallback = false

    const measure = () => {
        const viewportElement = viewportRef.value
        const contentElement = contentRef.value

        if (viewportElement) {
            availableWidth.value = viewportElement.getBoundingClientRect().width
        }

        if (contentElement) {
            contentHeight.value = Math.max(contentElement.scrollHeight, contentElement.offsetHeight)
        }
    }

    const cleanup = () => {
        resizeObserver?.disconnect()
        resizeObserver = null

        if (hasWindowResizeFallback && typeof window !== 'undefined') {
            window.removeEventListener('resize', measure)
            hasWindowResizeFallback = false
        }
    }

    const setupObservers = () => {
        cleanup()
        measure()

        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(measure)

            if (viewportRef.value) resizeObserver.observe(viewportRef.value)
            if (contentRef.value) resizeObserver.observe(contentRef.value)

            return
        }

        if (typeof window !== 'undefined') {
            window.addEventListener('resize', measure)
            hasWindowResizeFallback = true
        }
    }

    onMounted(() => {
        nextTick(setupObservers)
    })

    watch(
        [viewportRef, contentRef, logicalWidth],
        () => nextTick(setupObservers),
        { flush: 'post' },
    )

    onBeforeUnmount(cleanup)

    const scale = computed(() => calculateBetaCanvasScale(availableWidth.value, logicalWidth.value))
    const scaledWidth = computed(() => calculateBetaCanvasScaledDimension(logicalWidth.value, scale.value))
    const scaledHeight = computed(() => calculateBetaCanvasScaledDimension(contentHeight.value, scale.value))

    return {
        availableWidth,
        contentHeight,
        scale,
        scaledWidth,
        scaledHeight,
    }
}
